import { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/compat/router';
import { useFlags } from 'launchdarkly-react-client-sdk';

import useDispensary from 'src/dispensary/hooks/use-dispensary';
import usePaths from 'hooks/use-paths';
import { isBrowser } from 'utils/misc-utils';
import { AddressValidationReason, useValidateAddressLimits } from 'src/state/actions/address';
import { didStoreRoutingHandleSource, STORE_ROUTING_FLAG } from 'src/constants/store-routing';
import { useStoreRoutingOffer } from 'src/hooks/use-store-routing-offer';

import { useNearestDispensaryLocation } from './use-nearest-dispensary-location';

export function useLocations() {
  const [address, setAddress] = useState(null);
  const { dispensary } = useDispensary();
  const flags = useFlags();
  const routingEnabled = Boolean(flags[STORE_ROUTING_FLAG]);
  const offerStoreRoute = useStoreRoutingOffer();
  const validateAddressLimits = useValidateAddressLimits();

  const { href } = usePaths();
  const router = useRouter();
  const searchByDeliveryArea = router?.query?.searchMethod === 'deliveryArea';

  const {
    nearestDispensaryLocation,
    nearbyDispensaryLocations,
    getNearestDispensaryLocation,
    error,
    loading,
  } = useNearestDispensaryLocation(searchByDeliveryArea);

  const handleChangeAddress = useCallback(
    async (location) => {
      if (!location || !dispensary) {
        return;
      }

      if (searchByDeliveryArea && routingEnabled) {
        let validationResult;
        try {
          validationResult = await validateAddressLimits(location, dispensary);
        } catch {
          // Preserve the legacy locator path when current-store eligibility cannot be established.
        }

        if (validationResult?.reason === AddressValidationReason.OUTSIDE_DELIVERY_AREA) {
          const outcome = await offerStoreRoute({
            source: 'chain_locator',
            address: location,
            currentDispensary: dispensary,
          });
          if (didStoreRoutingHandleSource(outcome)) {
            return;
          }
        }
      }

      setAddress(location);

      getNearestDispensaryLocation({
        variables: {
          dispensaryFilter: {
            type: `Dispensary`,
            chain: dispensary.chain,
            nearLat: location.lat,
            nearLng: location.lng,
            city: location.city,
            destinationTaxState: location.state,
            destinationTaxZipcode: location.zipcode,
            // Cast a wide net server-side; the nearby list is capped to a 100-mile radius
            // client-side using each result's returned distance.
            distance: 1000000,
            deliversToAddress: searchByDeliveryArea,
            activeOnly: true,
            includeStealthMode: true,
            sortBy: `distance`,
          },
        },
      });
    },
    [
      dispensary,
      getNearestDispensaryLocation,
      offerStoreRoute,
      routingEnabled,
      searchByDeliveryArea,
      validateAddressLimits,
    ]
  );

  const handleClearAddress = () => setAddress(null);

  const hasErrors = (error || (dispensary && !dispensary?.chain)) && isBrowser();

  useEffect(() => {
    if (hasErrors && router?.isReady) {
      router.replace(href);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, hasErrors, router?.isReady]); // ignoring href here.

  useEffect(() => {
    if (searchByDeliveryArea && address && nearestDispensaryLocation && router?.isReady) {
      router.replace(`/embedded-menu/${nearestDispensaryLocation.cName}`);
    }
  }, [address, nearestDispensaryLocation, searchByDeliveryArea, router]);

  return {
    nearestDispensaryLocation,
    nearbyDispensaryLocations,
    handleChangeAddress,
    address,
    handleClearAddress,
    searchByDeliveryArea,
    hasErrors,
    loading,
  };
}
